/* Header presentation only; predictive-search retains query ownership. */
(() => {
  if (customElements.get('myart-header-search')) return;
  class MyArtHeaderSearch extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.details = this.querySelector('details');
      this.toggle = this.querySelector('summary');
      this.modal = this.querySelector('.search-modal');
      this.content = this.querySelector('.search-modal__content');
      this.input = this.querySelector('input[type="search"]');
      this.header = this.closest('.myart-nav__header, .header');
      this.menu = this.header?.querySelector('.myart-nav__desktop');
      this.mobile = matchMedia('(max-width: 989px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.toggle.setAttribute('role', 'button');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.addEventListener('click', event => {
        event.preventDefault();
        this.details.open ? this.close() : this.open();
      }, { signal });
      this.querySelector('.search-modal__close-button').addEventListener('click', () => this.close(), { signal });
      this.addEventListener('pointerdown', () => this.modal.classList.add('myart-search--pointer'), { signal, capture: true });
      this.addEventListener('keydown', event => {
        if (['Tab', 'ArrowDown', 'ArrowUp', 'Escape'].includes(event.key)) this.modal.classList.remove('myart-search--pointer');
        if (event.key === 'Escape' && this.details.open) { event.preventDefault(); event.stopPropagation(); this.close(); }
      }, { signal });
      document.addEventListener('click', event => {
        if (this.details.open && (!this.contains(event.target) || event.target.classList.contains('modal-overlay'))) this.close(false);
      }, { signal });
      const measure = () => { if (this.details.open) this.measure(); };
      window.addEventListener('resize', measure, { signal });
      window.visualViewport?.addEventListener('resize', measure, { signal });
      window.visualViewport?.addEventListener('scroll', measure, { signal });
      this.mobile.addEventListener('change', () => { if (this.details.open) this.close(false, { immediate: true }); }, { signal });
    }
    measure() {
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      const header = this.header?.getBoundingClientRect();
      const top = Math.max(viewportTop, header?.top || 0);
      if (!this.mobile.matches && this.menu && header) {
        const menu = this.menu.getBoundingClientRect();
        this.style.setProperty('--search-left', `${menu.left - header.left}px`);
        this.style.setProperty('--search-width', `${menu.width}px`);
        this.style.setProperty('--search-top', `${(header.height - 48) / 2}px`);
      } else {
        this.style.setProperty('--search-top', `${top}px`);
      }
      const fieldBottom = this.input.getBoundingClientRect().bottom;
      this.style.setProperty('--search-results-height', `${Math.max(0, viewportTop + viewportHeight - fieldBottom - 20)}px`);
    }
    open() {
      if (this.details.open) {
        if (!this.closing) return;
        const width = getComputedStyle(this.content).width;
        const opacity = getComputedStyle(this.content).opacity;
        this.motion?.cancel();
        this.closing = false;
        this.motion = this.content.animate([{ opacity, width }, { opacity: 1, width: '100%' }], { duration: 180, easing: 'cubic-bezier(.2, 0, 0, 1)' });
        this.input.focus({ preventScroll: true });
        return;
      }
      this.header?.querySelectorAll('[data-myart-desktop-disclosure][open]').forEach(item => { item.open = false; });
      this.details.open = true;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.header?.classList.add('myart-search-open');
      if (this.menu) this.menu.inert = true;
      if (this.mobile.matches) this.lockPage();
      this.measure();
      if (!this.reduced.matches) this.motion = this.content.animate([
        { opacity: 0, width: this.mobile.matches ? '82%' : '42%' },
        { opacity: 1, width: '100%' }
      ], { duration: 220, easing: 'cubic-bezier(.2, 0, 0, 1)' });
      trapFocus(this.content, this.input);
      this.input.focus({ preventScroll: true });
      this.measure();
    }
    lockPage() {
      if (this.pageLock) return;
      const body = document.body;
      const keys = ['position', 'top', 'left', 'right', 'width', 'overflow'];
      this.pageLock = { y: window.scrollY, values: keys.map(key => [key, body.style.getPropertyValue(key), body.style.getPropertyPriority(key)]) };
      body.style.position = 'fixed'; body.style.top = `${-this.pageLock.y}px`;
      body.style.left = '0'; body.style.right = '0'; body.style.width = '100%'; body.style.overflow = 'hidden';
      document.documentElement.classList.add('myart-search-page-locked');
    }
    unlockPage() {
      if (!this.pageLock) return;
      const { y, values } = this.pageLock;
      this.pageLock = null;
      values.forEach(([key, value, priority]) => value ? document.body.style.setProperty(key, value, priority) : document.body.style.removeProperty(key));
      document.documentElement.classList.remove('myart-search-page-locked');
      window.scrollTo({ top: y, behavior: 'instant' });
    }
    close(returnFocus = true, { immediate = false } = {}) {
      if (!this.details?.open) return;
      if (this.closing && !immediate) return;
      const finish = () => {
        this.motion?.cancel();
        this.motion = null;
        this.closing = false;
        this.querySelector('predictive-search')?.close();
        this.details.open = false;
        this.toggle.setAttribute('aria-expanded', 'false');
        this.header?.classList.remove('myart-search-open');
        if (this.menu) this.menu.inert = false;
        removeTrapFocus(returnFocus ? this.toggle : null);
        this.unlockPage();
      };
      if (immediate || this.reduced.matches || !this.content.animate) { finish(); return; }
      const width = getComputedStyle(this.content).width;
      const opacity = getComputedStyle(this.content).opacity;
      this.motion?.cancel();
      this.closing = true;
      const animation = this.content.animate([
        { opacity, width },
        { opacity: 0, width: this.mobile.matches ? '82%' : '42%' }
      ], { duration: 200, easing: 'cubic-bezier(.4, 0, 1, 1)', fill: 'forwards' });
      this.motion = animation;
      animation.onfinish = () => { if (this.motion === animation) finish(); };
    }
    disconnectedCallback() {
      this.close(false, { immediate: true }); this.controller?.abort(); this.controller = null;
    }
  }
  customElements.define('myart-header-search', MyArtHeaderSearch);
})();
